import { useMemo } from 'react'
import { useBookings } from './useBookings'
import { useTenant } from './useTenant'
import Fuse from 'fuse.js'
import { BookingCustomField } from '@xeetr/types/models'
import { isFoodBookingModel } from '@xeetr/types/utils'
import { dashboardSearchUrlAtom, dashboardRoomAtom } from '@/atoms'
import { useAtomValue } from 'jotai'

/**
 * Generates Fuse search keys for booking custom fields
 */
export const generateCustomFieldSearchKeys = (customFields?: BookingCustomField[]) => {
  if (!customFields?.length) return []

  return customFields.map((field) => ({
    name: `bookingAdditions.customFields.${field.id}`,
    weight: 1,
  }))
}

export function useFilteredBookings() {
  const { tenant } = useTenant()
  const filterText = useAtomValue(dashboardSearchUrlAtom)
  const selectedRoom = useAtomValue(dashboardRoomAtom)
  const { bookings } = useBookings()

  const fuseOptions = useMemo(
    () => ({
      keys: [
        { name: 'customer.firstName', weight: 2 },
        { name: 'customer.lastName', weight: 2 },
        { name: 'bookingAdditions.additionalInfo', weight: 1 },
        { name: 'bookingAdditions.note', weight: 1 },
        { name: 'room.name', weight: 1.5 },
        { name: 'room.tables.name', weight: 1 },
        // Add custom field search keys dynamically
        ...generateCustomFieldSearchKeys(tenant?.bookingCustomFields),
      ],
      threshold: 0.4, // Increased to be more lenient
      includeScore: true,
      includeMatches: true, // Include match information for debugging
      ignoreLocation: true,
      minMatchCharLength: 1,
      useExtendedSearch: false,
      findAllMatches: true,
      shouldSort: true, // Sort by relevance
      isCaseSensitive: false,
    }),
    [tenant]
  )

  const filteredBookings = useMemo(() => {
    let filtered = bookings || []

    if (selectedRoom && selectedRoom !== 'all') {
      filtered = filtered.filter(
        (booking) => isFoodBookingModel(booking) && booking.room?.id === selectedRoom
      )
    }

    if (filterText && filterText.trim()) {
      const fuse = new Fuse(filtered, fuseOptions)
      const tokens = filterText.trim().split(/\s+/)
      const pattern = tokens.map((token) => ({
        $or: [
          { 'customer.firstName': token },
          { 'customer.lastName': token },
          { 'bookingAdditions.additionalInfo': token },
          { 'bookingAdditions.note': token },
          { 'room.name': token },
          { 'room.tables.name': token },
          ...generateCustomFieldSearchKeys(tenant?.bookingCustomFields).map((key) => ({
            [key.name]: token,
          })),
        ],
      }))
      const results = fuse.search({ $and: pattern })

      filtered = results.map((result) => result.item)
    }
    return filtered
  }, [bookings, filterText, selectedRoom, fuseOptions])

  return {
    bookings: filteredBookings,
  }
}
